import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {client} from "@/httpClient";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore from "@/store/campaignManagement";
import type {TCampaignApplicationAccepted, TPaginated} from "@/store/types";
import {useMutation, useQueryClient, type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {produce} from "immer";
import cloneDeep from "lodash/cloneDeep";
import remove from "lodash/remove";

type TSendPackageVariables = {
  trackingCode?: string | null;
  trackingUrl?: string | null;
};

/**
 * Marks an application's package as sent (PATCH product_sent: true) and
 * optimistically moves the card from the "not sent" to the "sent" list.
 *
 * Tracking keys are only included in the payload when they are defined, so
 * the direct "Package sent" path (used when a tracking code already exists,
 * e.g. auto-extracted from a carrier email) never clears existing values.
 */
export const useSendPackageMutation = (applicationId: number, options?: {onSuccess?: () => void}) => {
  const queryClient = useQueryClient();
  const campaign = useCampaignManagementStore(state => state.campaign);
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);

  return useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplication(applicationId),
    mutationFn: (variables: TSendPackageVariables) =>
      client.patch(CAMPAIGN_APPLICATION_KEYS.mApplication(applicationId)[0], {
        product_sent: true,
        ...(variables.trackingCode !== undefined && {tracking_code: variables.trackingCode}),
        ...(variables.trackingUrl !== undefined && {tracking_url: variables.trackingUrl}),
      }),
    onMutate: variables => {
      const notSentQueryKey = CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam);
      const sentQueryKey = CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam);
      const previousNotSent =
        queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationAccepted>>>>(
          notSentQueryKey,
        );
      const previousSent =
        queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationAccepted>>>>(sentQueryKey);
      let application: TCampaignApplicationAccepted | undefined;

      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationAccepted>>>>(
        notSentQueryKey,

        prevState =>
          produce(prevState, draft => {
            for (const page of draft?.pages || []) {
              if (!application) {
                const removedApplication = remove(
                  page.data.results,
                  application => application.id === applicationId,
                )[0];
                application = cloneDeep(removedApplication);
                page.data.count--;
              }
            }
          }),
      );

      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationAccepted>>>>(
        sentQueryKey,
        prevState => {
          return produce(prevState, draft => {
            if (application && draft?.pages[0].data.results) {
              draft.pages[0].data.results = [
                produce(application, draft => {
                  draft.product_sent = true;
                  if (variables.trackingUrl !== undefined) draft.tracking_url = variables.trackingUrl;
                  if (variables.trackingCode !== undefined) draft.tracking_code = variables.trackingCode;
                }),
              ].concat(draft.pages[0].data.results);
              draft.pages[0].data.count++;
            }
          });
        },
      );

      return {notSentQueryKey, previousNotSent, previousSent, sentQueryKey};
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(context.notSentQueryKey, context.previousNotSent);
      queryClient.setQueryData(context.sentQueryKey, context.previousSent);
    },
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam),
      ].forEach(queryKey => queryClient.invalidateQueries({queryKey}));
      options?.onSuccess?.();
    },
  });
};
